<template>
  <!-- Modal - Vue controlled -->
  <div class="modal" tabindex="-1" id="jobUpdateModal" :class="{ show: isModalVisible }" :style="{ display: isModalVisible ? 'block' : 'none' }">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <div class="header-left">
            <h5 class="modal-title text-danger">{{ isEditMode ? 'Update Job Details' : 'Enter New Job Details' }}</h5>
          </div>
          <div class="header-right">
            <button type="button" class="btn btn-secondary btn-sm btn-header-action" @click="close">
              <i class="bi bi-x-circle"></i> Cancel
            </button>
            <button type="button" class="btn btn-success btn-sm btn-header-action" v-if="!isEditMode" @click="AddJob">
              <i class="bi bi-save"></i> Save Job
            </button>
            <button type="button" class="btn btn-primary btn-sm btn-header-action" v-if="isEditMode" @click="UpdateJob">
              <i class="bi bi-pencil"></i> Update Job
            </button>
          </div>
        </div>

        <div class="modal-body">
          <!-- Officer -->
          <div class="form-group-row">
            <label class="form-label">Officer</label>
            <div class="form-field">
              <select class="form-control" v-model="selectedOfficerId" @change="onOfficerChange">
                <option value="0" disabled>Select Officer...</option>
                <option v-for="officer in officers" :key="officer.id" :value="officer.id">
                  {{ officer.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Site -->
          <div class="form-group-row">
            <label class="form-label">Site</label>
            <div class="form-field">
              <select class="form-control" v-model="selectedSiteId" @change="onSiteChange">
                <option value="0" disabled>Select Site...</option>
                <option v-for="site in sites" :key="site.id" :value="site.id">
                  {{ site.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Client -->
          <div class="form-group-row">
            <label class="form-label">Client</label>
            <div class="form-field">
              <select class="form-control" v-model="selectedJobClientId" @change="OnClientChange">
                <option value="0" disabled>Select Client...</option>
                <option v-for="client in selectedSiteClients" :key="client.id" :value="client.id">
                  {{ client.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Status -->
          <div class="form-group-row">
            <label class="form-label">Status</label>
            <div class="form-field">
              <select class="form-control" v-model="selectedJobStatusId" @change="OnStatusChange">
                <option value="0" disabled>Select Status...</option>
                <option v-for="jobStatus in jobStatuses" :key="jobStatus.id" :value="jobStatus.id">
                  {{ jobStatus.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Job Date -->
          <div class="form-group-row">
            <label class="form-label">Job Date</label>
            <div class="form-field">
              <input type="date" v-model="selectedJobDate" class="form-control">
            </div>
          </div>

          <!-- Time - with proper underline -->
          <div class="form-group-row section-group">
            <label class="form-label section-label-wrapper">
              <span class="section-label">Time <span class="label-colon">:</span></span>
            </label>
            <div class="form-field">
              <div class="time-group">
                <div class="time-input">
                  <label class="sub-label">Start Time</label>
                  <input type="text" class="form-control" v-model="selectedJobStartTime" placeholder="HH:MM">
                </div>
                <div class="time-input">
                  <label class="sub-label">End Time</label>
                  <input type="text" class="form-control" v-model="selectedJobEndTime" placeholder="HH:MM">
                </div>
              </div>
            </div>
          </div>

          <!-- Financials - with proper underline -->
          <div class="form-group-row section-group">
            <label class="form-label section-label-wrapper">
              <span class="section-label">Financials <span class="label-colon">:</span></span>
            </label>
            <div class="form-field">
              <div class="financial-grid">
                <div class="financial-item">
                  <label class="sub-label">Pay Rate</label>
                  <input type="number" class="form-control" v-model="selectedJobPayRate" step="0.01" placeholder="0.00">
                </div>
                <div class="financial-item">
                  <label class="sub-label">Adjustment</label>
                  <input type="number" class="form-control" v-model="selectedJobAdjustment" step="0.01" placeholder="0.00">
                </div>
                <div class="financial-item">
                  <label class="sub-label">T. Exp.</label>
                  <input type="number" class="form-control" v-model="selectedJobTravelExpense" step="0.01" placeholder="0.00">
                </div>
                <div class="financial-item">
                  <label class="sub-label">B. Hrs.</label>
                  <input type="number" class="form-control" v-model="selectedJobBonusHours" step="0.01" placeholder="0.00">
                </div>
              </div>
            </div>
          </div>

          <!-- Remarks - without colon -->
          <div class="form-group-row">
            <label class="form-label">Remarks</label>
            <div class="form-field">
              <textarea class="form-control remarks-textarea" v-model="selectedJobRemarks" rows="3" placeholder="Enter any additional notes..."></textarea>
            </div>
          </div>

          <!-- LogView Component - Only show for existing jobs -->
          <div v-if="currentJobId > 0" class="log-section">
            <hr>
            <h6 class="text-muted">
              <i class="bi bi-clock-history"></i> Job History
            </h6>
            <LogView
              :key="refreshKey"
              :officers="officers"
              :baseUrl="baseUrl"
              :sites="sites"
              :jobStatuses="jobStatuses"
              :entityType="'Job'"
              :entityId="currentJobId"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Backdrop -->
  <div v-if="isModalVisible" class="modal-backdrop fade show"></div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import LogView from './LogView.vue';

export default defineComponent({
  name: "JobModal",
  components: {
    LogView
  },
  props: {
    baseUrl: { type: String, required: true },
    officers: { type: Array as PropType<any[]>, default: () => [] },
    sites: { type: Array as PropType<any[]>, default: () => [] },
    jobStatuses: { type: Array as PropType<any[]>, default: () => [] },
  },
  emits: ["job-added", "job-updated"],
  data() {
    return {
      isModalVisible: false,
      isEditMode: false,
      currentJobId: 0,
      refreshKey: 0,
      selectedOfficerId: 0,
      selectedSiteId: 0,
      selectedJobClientId: 0,
      selectedJobStatusId: 0,
      selectedJobDate: "",
      selectedJobStartTime: "09:00",
      selectedJobEndTime: "17:00",
      selectedJobRemarks: "",
      selectedJobAdjustment: 0,
      selectedJobTravelExpense: 0,
      selectedJobBonusHours: 0,
      selectedJobPayRate: 0,
      selectedSiteClients: [] as any[],
      rateForSelectedClientSite: 0
    };
  },
  methods: {
    dateToYYYYMMDD(d: Date) {
      if (!d) return "";
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split("T")[0];
    },
    
    showModal() {
      this.isModalVisible = true;
      document.body.classList.add("modal-open");
    },
    
    close() {
      this.isModalVisible = false;
      document.body.classList.remove("modal-open");
      this.resetForm();
    },
    
    openForAdd() {
      this.isEditMode = false;
      this.currentJobId = 0;
      this.resetForm();
      this.showModal();
    },
    
    openForEdit(job: any) {
      this.isEditMode = true;
      this.currentJobId = job.jobId;
      this.refreshKey++;
      this.loadJobData(job);
      this.showModal();
    },
    
    resetForm() {
      this.selectedOfficerId = 0;
      this.selectedSiteId = 0;
      this.selectedJobClientId = 0;
      this.selectedJobStatusId = 0;
      this.selectedJobDate = this.dateToYYYYMMDD(new Date());
      this.selectedJobStartTime = "09:00";
      this.selectedJobEndTime = "17:00";
      this.selectedJobRemarks = "";
      this.selectedJobAdjustment = 0;
      this.selectedJobTravelExpense = 0;
      this.selectedJobBonusHours = 0;
      this.selectedJobPayRate = 0;
      this.selectedSiteClients = [];
      this.rateForSelectedClientSite = 0;
    },

    loadJobData(job: any) {
      this.selectedOfficerId = job.securityOfficerId || 0;
      this.selectedSiteId = job.siteId || 0;
      this.selectedJobClientId = job.clientId || 0;
      this.selectedJobStatusId = job.statusId || 0;
      this.selectedJobDate = job.jobDate ? job.jobDate.split('T')[0] : this.dateToYYYYMMDD(new Date());
      this.selectedJobStartTime = this.formatTimeString(job.startTime);
      this.selectedJobEndTime = this.formatTimeString(job.endTime);
      this.selectedJobRemarks = job.remarks || "";
      this.selectedJobAdjustment = job.adjustmentHours || 0;
      this.selectedJobTravelExpense = job.travelExpense || 0;
      this.selectedJobBonusHours = job.bonusHours || 0;
      this.selectedJobPayRate = job.payRate || 0;
      
      if (this.selectedSiteId) {
        this.fetchSiteClients(this.selectedSiteId);
      }
    },

    formatTimeString(time: any): string {
      if (!time) return "09:00";
      if (typeof time === 'string') return time.substring(0, 5);
      if (typeof time === 'object' && time.Hours !== undefined) {
        return `${time.Hours.toString().padStart(2, '0')}:${time.Minutes.toString().padStart(2, '0')}`;
      }
      return "09:00";
    },

    fetchSiteClients(siteId: number) {
      fetch(`${this.baseUrl}AppData/GetClientsForSelectedSite/${siteId}`, {
        headers: { Authorization: `Bearer ${localStorage.token}` }
      })
      .then(r => r.json())
      .then(res => {
        this.selectedSiteClients = res.clients || [];
        if (this.selectedJobClientId) {
          const client = this.selectedSiteClients.find(sc => sc.id == this.selectedJobClientId);
          this.rateForSelectedClientSite = client?.rate || 0;
        }
      })
      .catch(error => console.error("Error fetching site clients:", error));
    },

    onOfficerChange() {
      //console.log("Selected Officer ID:", this.selectedOfficerId);
    },
    
    onSiteChange() {
      this.selectedJobClientId = 0;
      this.rateForSelectedClientSite = 0;
      if (this.selectedSiteId) {
        this.fetchSiteClients(this.selectedSiteId);
      } else {
        this.selectedSiteClients = [];
      }
    },
    
    OnClientChange() {
      this.rateForSelectedClientSite = this.selectedSiteClients.find(sc => sc.id == this.selectedJobClientId)?.rate || 0;
    },
    
    OnStatusChange() {
      //console.log("Selected Status ID:", this.selectedJobStatusId);
    },
    

AddJob() {
  if (!this.selectedOfficerId || !this.selectedSiteId || !this.selectedJobClientId || !this.selectedJobStatusId) {
    alert("Please fill all required fields");
    return;
  }

  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timeRegex.test(this.selectedJobStartTime) || !timeRegex.test(this.selectedJobEndTime)) {
    alert("Please enter valid time in HH:MM format");
    return;
  }

  const newJob = {
    jobId: 0,
    securityOfficerId: this.selectedOfficerId,
    siteId: this.selectedSiteId,
    clientId: this.selectedJobClientId,
    statusId: this.selectedJobStatusId,
    jobDate: this.selectedJobDate,
    startTime: this.selectedJobStartTime + ":00",
    endTime: this.selectedJobEndTime + ":00",
    payRate: parseFloat(this.selectedJobPayRate as any) || 0,
    rate: this.rateForSelectedClientSite || 0,
    adjustment: parseFloat(this.selectedJobAdjustment as any) || 0,
    travelExpense: parseFloat(this.selectedJobTravelExpense as any) || 0,
    bonusHours: parseFloat(this.selectedJobBonusHours as any) || 0,
    remarks: this.selectedJobRemarks,
    operator: localStorage.getItem("userId"),
    recordDateTime: new Date().toISOString(),
    recordStatusId: 1
  };

  fetch(`${this.baseUrl}schedule/AddJob`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${localStorage.token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(newJob)
  })
  .then((r: Response) => {
    if (r.status === 200 || r.status === 204) {
      if (r.status === 204 || r.headers.get('content-length') === '0') {
        return { success: true };
      }
      return r.json();
    } else {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
  })
  .then((result: any) => {
    if (result && result.success !== false) {
      alert("Job added successfully!");
      this.close();
      this.$emit("job-added");
    } else {
      alert("Error adding job");
    }
  })
  .catch((error: Error) => {
    console.error("Error adding job:", error);
    alert(`Error adding job: ${error.message}`);
  });
},

    // ============ FIXED UpdateJob Method ============
    UpdateJob() {
  if (!this.selectedOfficerId || !this.selectedSiteId || !this.selectedJobClientId || !this.selectedJobStatusId) {
    alert("Please fill all required fields");
    return;
  }

  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timeRegex.test(this.selectedJobStartTime) || !timeRegex.test(this.selectedJobEndTime)) {
    alert("Please enter valid time in HH:MM format");
    return;
  }

  const updatedJob = {
    jobId: this.currentJobId,
    securityOfficerId: this.selectedOfficerId,
    siteId: this.selectedSiteId,
    clientId: this.selectedJobClientId,
    statusId: this.selectedJobStatusId,
    jobDate: this.selectedJobDate,
    startTime: this.selectedJobStartTime + ":00",
    endTime: this.selectedJobEndTime + ":00",
    payRate: parseFloat(this.selectedJobPayRate as any) || 0,
    rate: this.rateForSelectedClientSite || 0,
    adjustment: parseFloat(this.selectedJobAdjustment as any) || 0,
    travelExpense: parseFloat(this.selectedJobTravelExpense as any) || 0,
    bonusHours: parseFloat(this.selectedJobBonusHours as any) || 0,
    remarks: this.selectedJobRemarks,
    operator: localStorage.getItem("userId"),
    recordDateTime: new Date().toISOString(),
    recordStatusId: 2
  };

  //console.log("updatedJob = " , updatedJob);

  fetch(`${this.baseUrl}schedule/UpdateJob`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${localStorage.token}`,
      "Content-Type": "application/json",
      //"Origin": "https://testadmin.emergingsols.co.uk", // Browser adds this automatically
    },
    body: JSON.stringify(updatedJob)
  })
  .then((r: Response) => {
    if (r.status === 200 || r.status === 204) {
      if (r.status === 204 || r.headers.get('content-length') === '0') {
        return { success: true };
      }
      return r.json();
    } else {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
  })
  .then((result: any) => {
    if (result && result.success !== false) {
      alert("Job updated successfully!");
      this.refreshKey++;
      this.$emit("job-updated");
      this.close();
    } else {
      alert("Error updating job");
    }
  })
  .catch((error: Error) => {
    console.error("Error updating job:", error);
    alert(`Error updating job: ${error.message}`);
  });
}

    // ============ END OF FIXED UpdateJob Method ============
  }
});
</script>

<style scoped>
/* Modal styles */
.modal {
  z-index: 1050;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;
}

.modal.show {
  display: block;
  background-color: rgba(0,0,0,0.5);
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.5);
  z-index: 1040;
}

body.modal-open {
  overflow: hidden;
}

.modal-lg {
  max-width: 800px;
}

/* Modal Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 2px solid #e9ecef;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.btn-close {
  display: none;
}

/* Header action buttons */
.btn-header-action {
  padding: 0.375rem 1rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  min-height: 36px;
}

.btn-header-action i {
  font-size: 0.9rem;
}

/* Form Group Row */
.form-group-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 1.25rem;
  gap: 0.5rem;
}

/* Section groups */
.section-group {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 2px solid #f0f0f0;
}

.form-label {
  font-weight: 600;
  font-size: 0.95rem;
  color: #2c3e50;
  margin-bottom: 0;
  letter-spacing: 0.3px;
}

/* Section label wrapper - controls the underline */
.section-label-wrapper {
  display: inline-block;
  width: auto;
}

/* Section labels with underline ONLY under the text */
.section-label {
  font-weight: 700;
  font-size: 1rem;
  color: #2c3e50;
  display: inline-block;
  border-bottom: 2px solid #007bff;
  padding-bottom: 2px;
  line-height: 1.4;
}

.label-colon {
  font-weight: 700;
  color: #007bff;
  margin-left: 2px;
}

.form-field {
  width: 100%;
}

/* Form controls */
.form-control {
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

select.form-control {
  appearance: auto;
  cursor: pointer;
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

/* Remarks textarea */
.remarks-textarea {
  width: 100%;
  min-height: 80px;
}

/* Sub-labels */
.sub-label {
  display: block;
  font-weight: 600;
  font-size: 0.85rem;
  color: #2c3e50;
  margin-bottom: 0.3rem;
  letter-spacing: 0.2px;
}

/* Time group */
.time-group {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.time-input {
  flex: 1;
  min-width: 120px;
}

/* Financial grid */
.financial-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.financial-item {
  display: flex;
  flex-direction: column;
}

.financial-item .sub-label {
  margin-bottom: 0.3rem;
}

/* Log section */
.log-section {
  margin-top: 1.5rem;
}

/* DESKTOP STYLES */
@media (min-width: 769px) {
  .form-group-row {
    flex-direction: row;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .form-label {
    width: 120px;
    flex-shrink: 0;
    text-align: right;
    padding-top: 0.5rem;
    font-size: 0.95rem;
  }
  
  .section-label-wrapper {
    display: block;
    width: 120px;
    text-align: right;
    padding-top: 0.5rem;
  }
  
  .section-label {
    font-size: 1rem;
    border-bottom: 2px solid #007bff;
    padding-bottom: 2px;
  }
  
  .form-field {
    flex: 1;
  }
  
  .financial-grid {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
  
  .time-group {
    flex-wrap: nowrap;
  }
  
  .sub-label {
    font-size: 0.9rem;
    font-weight: 600;
    color: #2c3e50;
  }
  
  .section-group {
    margin-top: 0.5rem;
    padding-top: 0.75rem;
  }
  
  .modal-header {
    padding: 1rem 1.5rem;
  }
  
  .btn-header-action {
    padding: 0.4rem 1.2rem;
    font-size: 0.9rem;
  }
}

/* TABLET STYLES */
@media (min-width: 577px) and (max-width: 768px) {
  .modal-dialog {
    margin: 1rem;
  }
  
  .financial-grid {
    grid-template-columns: 1fr 1fr;
  }
  
  .form-label {
    font-size: 0.95rem;
  }
  
  .section-label-wrapper {
    display: block;
    width: 120px;
    text-align: right;
    padding-top: 0.5rem;
  }
  
  .section-label {
    font-size: 1rem;
    border-bottom: 2px solid #007bff;
    padding-bottom: 2px;
  }
  
  .sub-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #2c3e50;
  }
  
  .section-group {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
  }
  
  .modal-header {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
  
  .header-left {
    justify-content: center;
  }
  
  .header-right {
    justify-content: center;
  }
  
  .btn-header-action {
    padding: 0.35rem 1rem;
    font-size: 0.85rem;
  }
}

/* MOBILE STYLES */
@media (max-width: 576px) {
  .modal-dialog {
    margin: 0.5rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .modal-header {
    padding: 0.75rem 1rem;
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
  
  .header-left {
    justify-content: center;
  }
  
  .header-right {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .modal-title {
    font-size: 1.1rem;
    text-align: center;
  }
  
  .form-group-row {
    margin-bottom: 1.25rem;
    gap: 0.4rem;
  }
  
  .form-label {
    font-size: 0.9rem;
    font-weight: 700;
    color: #2c3e50;
    text-align: left;
    padding: 0;
  }
  
  .section-label-wrapper {
    display: inline-block;
    width: auto;
    text-align: left;
    padding: 0;
  }
  
  .section-label {
    font-size: 0.95rem;
    font-weight: 700;
    display: inline-block;
    border-bottom: 2px solid #007bff;
    padding-bottom: 2px;
  }
  
  .form-control {
    font-size: 16px !important;
    padding: 0.6rem 0.75rem;
  }
  
  select.form-control {
    font-size: 16px !important;
  }
  
  textarea.form-control {
    font-size: 16px !important;
  }
  
  .sub-label {
    font-size: 0.9rem;
    font-weight: 700;
    color: #2c3e50;
    text-align: left;
    margin-bottom: 0.25rem;
  }
  
  .time-group {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .time-input {
    width: 100%;
    min-width: unset;
  }
  
  .financial-grid {
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }
  
  select.form-control,
  input.form-control,
  textarea.form-control {
    min-height: 44px;
  }
  
  .section-group {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
  }
  
  .btn-header-action {
    padding: 0.4rem 1rem;
    font-size: 0.85rem;
    flex: 1;
    justify-content: center;
    min-height: 40px;
  }
  
  .btn-header-action i {
    font-size: 0.85rem;
  }
}

/* LogView styles */
.log-container {
  border: 1px solid #ddd;
  border-radius: 4px;
  max-height: 300px;
  overflow-y: auto;
  background: #fff;
}

.log-row {
  padding: 8px 12px;
  font-size: 13px;
  font-family: monospace;
  border-bottom: 1px solid #eee;
}

.log-even {
  background-color: #f9f9f9;
}

.log-odd {
  background-color: #ffffff;
}

.log-row:hover {
  background-color: #f0f0f0;
}

.log-empty {
  padding: 20px;
  text-align: center;
  color: #999;
  font-style: italic;
}

.reason-text {
  color: #0066cc;
}

/* Modal animation */
.modal.show {
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Fix for Bootstrap icon spacing */
.bi {
  vertical-align: middle;
}

/* Button hover effects */
.btn-header-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  transition: all 0.2s ease;
}

.btn-header-action:active {
  transform: translateY(0);
}

/* Button colors */
.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  color: #fff;
}

.btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
  color: #fff;
}

.btn-success:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

.btn-primary:hover {
  background-color: #0069d9;
  border-color: #0062cc;
}
</style>